import type { ReactNode } from "react";
import React from "react";
import {
	Menu as AriaMenu,
	MenuItem as AriaMenuItem,
	MenuTrigger as AriaMenuTrigger,
	Popover as AriaPopover,
	MenuSection as AriaSection,
	Separator as AriaSeparator,
	type MenuProps as AriaMenuProps,
	type MenuItemProps as AriaMenuItemProps,
	type MenuTriggerProps as AriaMenuTriggerProps,
	type PopoverProps as AriaPopoverProps,
	type SectionProps as AriaSectionProps,
	Header,
	composeRenderProps,
	Button,
} from "react-aria-components";
import { NavLink } from "react-router";
import { tw } from "../../styling";

export const MenuTrigger = (props: AriaMenuTriggerProps) => <AriaMenuTrigger {...props} />;

export const MenuPopover = ({ className, ...props }: AriaPopoverProps) => (
	<AriaPopover
		{...props}
		className={composeRenderProps(className, (className) =>
			tw(
				"bg-background-primary ring-1",
				"shadow-bistron ring-background-divider rounded-md",
				"fixed inset-x-2 top-2 w-auto",
				"max-h-[calc(100vh-1rem)] overflow-auto",
				"tablet:relative tablet:inset-auto",
				"tablet:max-h-none tablet:overflow-visible",
				className,
			),
		)}
	/>
);

export const Menu = <T extends object>(props: AriaMenuProps<T>) => (
	<AriaMenu {...props} className={tw("outline-hidden")} />
);

export const MenuItem = <T extends object>({ ...props }: AriaMenuItemProps<T>) => (
	<AriaMenuItem
		{...props}
		className={({ isFocused, isSelected }) =>
			tw(
				"flex w-full cursor-pointer items-center p-4 outline-hidden select-none",
				isFocused && "bg-background-tertiary",
				isSelected && "font-medium",
				props.className as string,
			)
		}
		render={(domProps) => {
			if ("href" in domProps && domProps.href) {
				return <NavLink to={domProps.href} {...domProps} />;
			}

			return <div {...(domProps as React.HTMLAttributes<HTMLDivElement>)} />;
		}}
	/>
);

export const MenuSection = <T extends object>(props: AriaSectionProps<T>) => (
	<AriaSection {...props} />
);

export const MenuHeader = ({ children }: { children: ReactNode }) => (
	<Header className={tw("px-4 pt-4 pb-2")}>{children}</Header>
);

export const MenuSeparator = () => <AriaSeparator className={tw("h-px bg-gray-200")} />;

export const MenuPressable = ({ label, children }: { label?: string; children: ReactNode }) => (
	<Button
		aria-label={label}
		className={`focus-visible:outline-interactive-primary overflow-hidden rounded-md focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-solid`}
	>
		{children}
	</Button>
);
